import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const CLICK_SCALE = 2.5;

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

/**
 * Tam ekran şəkil baxışı.
 *  - Klik / iki dəfə toxunma: böyüt / kiçilt
 *  - Böyüdülmüş halda: sürüşdür
 *  - Mobil: iki barmaqla böyüt (pinch)
 *  - Klaviatura: ← → keçid, Esc bağla, + - böyüt
 */
const ImageLightbox = ({ images = [], startIndex = 0, open, onClose, alt = '' }) => {
  const [index, setIndex] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const stageRef = useRef(null);
  const pointers = useRef(new Map());
  const pinchStart = useRef(null);
  const dragStart = useRef(null);
  const moved = useRef(false);
  const lastTap = useRef(0);

  const count = images.length;

  const reset = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Açılanda başlanğıc şəkil
  useEffect(() => {
    if (open) {
      setIndex(clamp(startIndex, 0, Math.max(0, count - 1)));
      reset();
    }
  }, [open, startIndex, count, reset]);

  // Şəkil dəyişəndə zoom sıfırlansın
  useEffect(() => {
    reset();
  }, [index, reset]);

  // Arxa fon sürüşməsin
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const go = useCallback(
    (dir) => {
      if (count < 2) return;
      setIndex((i) => (i + dir + count) % count);
    },
    [count]
  );

  // Böyüdülmüş şəkil ekrandan tamamilə çıxmasın
  const clampOffset = useCallback((next, s) => {
    const stage = stageRef.current;
    if (!stage || s <= 1) return { x: 0, y: 0 };
    const rect = stage.getBoundingClientRect();
    const maxX = ((s - 1) * rect.width) / 2;
    const maxY = ((s - 1) * rect.height) / 2;
    return { x: clamp(next.x, -maxX, maxX), y: clamp(next.y, -maxY, maxY) };
  }, []);

  const zoomAt = useCallback(
    (targetScale, clientX, clientY) => {
      const stage = stageRef.current;
      const s = clamp(targetScale, MIN_SCALE, MAX_SCALE);
      if (!stage || s === 1) {
        setScale(1);
        setOffset({ x: 0, y: 0 });
        return;
      }
      const rect = stage.getBoundingClientRect();
      const cx = clientX ?? rect.left + rect.width / 2;
      const cy = clientY ?? rect.top + rect.height / 2;
      // Kliklənən nöqtə mərkəzə gəlsin
      const dx = rect.left + rect.width / 2 - cx;
      const dy = rect.top + rect.height / 2 - cy;
      setScale(s);
      setOffset(clampOffset({ x: dx * (s - 1), y: dy * (s - 1) }, s));
    },
    [clampOffset]
  );

  // Klaviatura
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === '+' || e.key === '=') zoomAt(Math.min(MAX_SCALE, scale + 0.75));
      else if (e.key === '-') zoomAt(Math.max(MIN_SCALE, scale - 0.75));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, go, zoomAt, scale]);

  const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    moved.current = false;

    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinchStart.current = { dist: distance(a, b), scale };
      dragStart.current = null;
    } else if (pointers.current.size === 1) {
      dragStart.current = { x: e.clientX, y: e.clientY, offset: { ...offset } };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // İki barmaq — pinch
    if (pointers.current.size === 2 && pinchStart.current) {
      const [a, b] = [...pointers.current.values()];
      const ratio = distance(a, b) / pinchStart.current.dist;
      const s = clamp(pinchStart.current.scale * ratio, MIN_SCALE, MAX_SCALE);
      setScale(s);
      setOffset((o) => clampOffset(o, s));
      moved.current = true;
      return;
    }

    // Bir barmaq / siçan — sürüşdürmə
    if (pointers.current.size === 1 && dragStart.current) {
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) moved.current = true;

      if (scale > 1) {
        setOffset(
          clampOffset(
            { x: dragStart.current.offset.x + dx, y: dragStart.current.offset.y + dy },
            scale
          )
        );
      }
    }
  };

  const onPointerUp = (e) => {
    const start = dragStart.current;
    pointers.current.delete(e.pointerId);

    if (pointers.current.size < 2) pinchStart.current = null;

    // Normal ölçüdə üfüqi sürüşdürmə -> növbəti / əvvəlki şəkil
    if (start && scale === 1 && pointers.current.size === 0) {
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
        go(dx < 0 ? 1 : -1);
        dragStart.current = null;
        return;
      }
    }

    if (pointers.current.size === 0) {
      // Hərəkətsiz toxunma = klik
      if (!moved.current) {
        const now = Date.now();
        const isTouch = e.pointerType === 'touch';

        if (isTouch) {
          // Mobil: iki dəfə toxun
          if (now - lastTap.current < 300) {
            zoomAt(scale > 1 ? 1 : CLICK_SCALE, e.clientX, e.clientY);
            lastTap.current = 0;
          } else {
            lastTap.current = now;
          }
        } else {
          // Desktop: bir klik
          zoomAt(scale > 1 ? 1 : CLICK_SCALE, e.clientX, e.clientY);
        }
      }
      dragStart.current = null;
    }
  };

  const onWheel = (e) => {
    const delta = e.deltaY < 0 ? 0.4 : -0.4;
    zoomAt(scale + delta, e.clientX, e.clientY);
  };

  if (!open || count === 0) return null;

  const current = images[index];

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Şəkil baxışı"
    >
      {/* Üst panel */}
      <div
        className="flex items-center justify-between px-4 py-3 text-white flex-shrink-0"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <span className="text-xs tracking-widest text-white/60">
          {count > 1 ? `${index + 1} / ${count}` : ''}
        </span>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => zoomAt(scale - 0.75)}
            disabled={scale <= MIN_SCALE}
            aria-label="Kiçilt"
            className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 disabled:opacity-30 transition-colors"
          >
            <ZoomOut className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => zoomAt(scale + 0.75)}
            disabled={scale >= MAX_SCALE}
            aria-label="Böyüt"
            className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 disabled:opacity-30 transition-colors"
          >
            <ZoomIn className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Bağla"
            className="flex items-center justify-center w-11 h-11 rounded-full hover:bg-white/10 transition-colors"
          >
            <X className="w-6 h-6" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Şəkil sahəsi */}
      <div className="relative flex-1 min-h-0 flex items-center justify-center overflow-hidden">
        <div
          ref={stageRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onWheel={onWheel}
          className={`relative w-full h-full flex items-center justify-center ${
            scale > 1 ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in'
          }`}
          style={{ touchAction: 'none' }}
        >
          <img
            src={current}
            alt={alt}
            draggable={false}
            className="max-w-full max-h-full object-contain select-none"
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              transition: pointers.current.size > 0 ? 'none' : 'transform 0.25s ease-out',
            }}
          />
        </div>

        {count > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Əvvəlki"
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <ChevronLeft className="w-6 h-6" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Növbəti"
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <ChevronRight className="w-6 h-6" strokeWidth={1.5} />
            </button>
          </>
        )}
      </div>

      {/* Alt: kiçik şəkillər + ipucu */}
      <div
        className="flex-shrink-0 px-4 pt-3 text-center"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      >
        {count > 1 && (
          <div className="flex justify-center gap-2 mb-3 overflow-x-auto">
            {images.map((img, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Şəkil ${i + 1}`}
                className={`flex-shrink-0 w-12 h-14 rounded overflow-hidden border-2 transition-all ${
                  i === index ? 'border-white opacity-100' : 'border-transparent opacity-50 hover:opacity-80'
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" draggable={false} />
              </button>
            ))}
          </div>
        )}
        <p className="text-[11px] text-white/40 tracking-wide">
          <span className="hidden sm:inline">Klik edin və ya siçan təkərini fırladın</span>
          <span className="sm:hidden">İki dəfə toxunun və ya iki barmaqla böyüdün</span>
        </p>
      </div>
    </div>
  );
};

export default ImageLightbox;
